import React, { useEffect, useLayoutEffect } from 'react'
import Tooltip from '@material-ui/core/Tooltip'
import CloseIcon from '@material-ui/icons/Close'
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import { AdCreative, AdCreativeType, FBAdPreviewFormat } from '@marketing-milk/interfaces'
import { useGetBusinessIfExists } from '@marketing-milk/frontend'
import { CreativePreview, getIFrameSource, isLocallyRenderable } from 'components/ad-preview'
import { useFetchCreativePreview } from 'hooks/creatives/fetch-creative-preview'
import { isDynamicCreative } from '../../campaigns/helpers/campaign.helpers'
import { radius, status, SurfacePalette, type as typo } from '../tokens'
import { CreativeJourneyPanel, getCreativeJourney } from './CreativeJourney'
import { DynamicAssetPool } from './DynamicAssetPool'
import { PacketModalShell } from './PacketModalShell'

interface Props {
  creative: AdCreative
  /**
   * True once the host has refetched the creatives since this modal opened, so the
   * `creative` fields are confirmed current. Gates approval against the local
   * (Facebook-independent) fallback — see the approve gate below.
   */
  creativeFresh: boolean
  /**
   * Dynamic-only: true when the latest sprint-creative refetch FAILED (vs merely
   * still in flight). Drives the dynamic approve tooltip and the inline retry
   * banner so a failed refresh can't enable approval from stale cached assets.
   */
  refetchFailed?: boolean
  /** Dynamic-only: re-run the sprint-creative refetch (retry after a failure). */
  onRetryFresh?: () => void
  palette: SurfacePalette
  onClose: () => void
  /** Edit-mode only: open the creative's native editor for source edits. */
  onOpenSource?: (creative: AdCreative) => void
}

const PREVIEW_WIDTH = 380
/** Fits the LeadCardPreview phone frame (340px + 24px border) with breathing room. */
const JOURNEY_WIDTH = 372

/**
 * Whether client approval can be recorded from the preview. Enabled when EITHER
 * the live FB preview is fully refreshed (original anti-stale path), OR the
 * creative is single-image/-video, its fields have been refetched since the modal
 * opened (`creativeFresh`), and the local fallback is therefore rendering current
 * fields. Carousel/dynamic only get a non-authoritative placeholder, so they are
 * approvable solely via a fresh FB preview — never from the local placeholder.
 */
export function isApproveEnabled(input: {
  format: AdCreativeType
  creativeFresh: boolean
  freshPreviewReady: boolean
  loadingPreview: boolean
  refreshing: boolean
}): boolean {
  const fbFresh = input.freshPreviewReady && !input.loadingPreview && !input.refreshing
  const canApproveLocally = isLocallyRenderable(input.format) && input.creativeFresh
  return fbFresh || canApproveLocally
}

/** A 36px icon button for the preview's side rail; tooltip carries the label. */
const RailIcon = ({
  palette,
  title,
  onClick,
  accent,
  danger,
  disabled,
  commentTrigger,
  children,
}: {
  palette: SurfacePalette
  title: string
  onClick?: () => void
  /** Primary action (filled accent) — Approve. */
  accent?: boolean
  /** Destructive action (red-tinted outline, not filled) — Remove approval. */
  danger?: boolean
  /** Greys out and blocks the action (e.g. Approve while a fresh preview loads). */
  disabled?: boolean
  /** Marks this as the shared comment trigger picked up by `useCommentAnchors`. */
  commentTrigger?: boolean
  children: React.ReactNode
}) => (
  <Tooltip title={title} placement="right">
    <span>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={title}
        data-comment-trigger={commentTrigger ? 'true' : undefined}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          padding: 0,
          border: `1px solid ${
            accent ? palette.accent : danger ? status.danger : palette.hairline
          }`,
          background: accent ? palette.accent : 'transparent',
          color: accent ? '#fff' : danger ? status.danger : palette.textMuted,
          borderRadius: radius.sm,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.4 : 1,
          lineHeight: 0,
        }}
      >
        {children}
      </button>
    </span>
  </Tooltip>
)

/**
 * Packet-styled dialog that renders the live Facebook mobile-feed Ad Preview for
 * a single sprint creative, reusing the Creative Wizard preview pipeline
 * (`useFetchCreativePreview` -> `MobilePreview`). The preview is the only surface
 * that shows the full ad (copy + headline + CTA, not just the image), so the
 * meeting-time actions live on a slim icon rail beside it — Comment (on the
 * creative object), Approve / Remove approval, Open in Creative Wizard, and Close
 * — each labelled via tooltip. A thin icon rail keeps the chrome out of the
 * vertical budget (so the whole ad fits without scrolling) and barely offsets the
 * ad from center. Mounted on demand from the Creative section in both Edit and
 * Present; the hosting `useCreativePreview` hook locks Present deck navigation
 * while it is open.
 *
 * The rail is wrapped in the `creative` comment anchor so the shared
 * `useCommentAnchors` delegation opens the same thread as the card — the preview
 * modal is a DOM descendant of the comment root, so the click bubbles normally.
 */
export function CreativePreviewModal({
  creative,
  creativeFresh,
  refetchFailed,
  onRetryFresh,
  palette,
  onClose,
  onOpenSource,
}: Props) {
  // Dynamic creatives swap the FB-iframe preview for the grouped asset pool, which
  // renders entirely from hydrated fields — so they never touch Facebook (the FB
  // effects/source below all early-return / no-op for dynamic).
  const isDynamic = isDynamicCreative(creative)
  const business = useGetBusinessIfExists()
  // The creative's post-click journey (v1.5b): a lead-form ad embeds the lead
  // card preview beside the ad; anything with a landing page gets a clickable
  // link card. Rendered as a second column for the standard preview, or as a
  // slim strip above the (already two-pane) dynamic asset pool.
  const journey = getCreativeJourney(creative)

  const { refreshPreview, adCreativePreview, loadingPreview, refreshing, freshPreviewReady } =
    useFetchCreativePreview(creative.id, FBAdPreviewFormat.MOBILE_FEED_STANDARD, {
      // We surface "unavailable" inline (banner + Retry) over the local fallback,
      // so the hook's error toast would be redundant and contradictory here.
      suppressErrorToast: true,
    })

  // Force a live refresh on mount: a hover-prefetched preview shows instantly, but
  // the creative may have been edited since, so we always refetch fresh (keeping
  // the cached HTML on screen until it arrives) before the AE can approve it.
  // useLayoutEffect so `refreshing` flips true before the first paint, leaving no
  // frame where the cached preview is shown with Approve still enabled.
  useLayoutEffect(() => {
    if (isDynamic) return
    refreshPreview()
  }, [refreshPreview, isDynamic])

  // When the packet tab regains focus (the AE returns from editing in the wizard
  // tab), refetch so the preview reflects the saved edit.
  useEffect(() => {
    // Dynamic's focus/visibility refresh is the sprint-creative refetch owned by
    // `useCreativePreview` (no FB preview to refresh), so skip the FB path here.
    if (isDynamic) return
    const onFocus = () => refreshPreview()
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshPreview()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [refreshPreview, isDynamic])

  // Only surface the FB iframe once the live refresh has confirmed it fresh. The
  // hook keeps any hover-prefetched HTML on screen while refreshing, but that copy
  // may predate an edit — rendering it into the (reveal-on-load) iframe could flash
  // stale FB content over the fresh local fallback.
  const fbFresh = freshPreviewReady && !loadingPreview && !refreshing
  const iframeSource = fbFresh && adCreativePreview ? getIFrameSource(adCreativePreview) : ''

  const rail = (
    <div
      data-comment-anchor-type="creative"
      data-comment-anchor-id={String(creative.id)}
      data-comment-label={creative.name}
      style={{ width: 36, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <RailIcon palette={palette} title="Close" onClick={onClose}>
        <CloseIcon style={{ fontSize: 18 }} />
      </RailIcon>
      <RailIcon palette={palette} title="Comment" commentTrigger>
        <ChatBubbleOutlineIcon style={{ fontSize: 16 }} />
      </RailIcon>
      {onOpenSource && (
        <RailIcon
          palette={palette}
          title="Open in Creative Wizard"
          onClick={() => onOpenSource(creative)}
        >
          <OpenInNewIcon style={{ fontSize: 16 }} />
        </RailIcon>
      )}
    </div>
  )

  return (
    <>
      <PacketModalShell
        palette={palette}
        hideHeader
        padding={12}
        onClose={onClose}
        // The two-pane asset pool (large media stage + strip + a text comparison
        // column) needs far more room than the single-column FB feed preview;
        // capped so it still fits 1440×900 with margin and stays centered.
        maxWidth={isDynamic ? 1040 : journey ? 464 + 12 + JOURNEY_WIDTH : 464}
        closeOnBackdropClick
      >
        <div style={{ display: 'flex', gap: 12 }}>
          {rail}
          {isDynamic ? (
            // Bound the pool's height and let each pane scroll independently inside
            // it. The rail stays outside so Close / Comment / Approve stay reachable
            // when either pane overflows.
            <div
              style={{
                flex: 1,
                minWidth: 0,
                maxHeight: 'min(820px, 88vh)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {refetchFailed && (
                <div
                  style={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    marginBottom: 14,
                    padding: '10px 12px',
                    borderRadius: radius.sm,
                    border: `1px solid ${status.danger}`,
                    background: 'rgba(253, 57, 122, 0.08)',
                  }}
                >
                  <span
                    style={{
                      fontFamily: typo.family.sans,
                      fontSize: typo.size.body,
                      color: palette.text,
                    }}
                  >
                    Latest assets could not be loaded.
                  </span>
                  {onRetryFresh && (
                    <button
                      type="button"
                      onClick={onRetryFresh}
                      style={{
                        background: 'transparent',
                        border: `1px solid ${palette.hairline}`,
                        borderRadius: radius.sm,
                        color: palette.text,
                        cursor: 'pointer',
                        padding: '4px 12px',
                        fontFamily: typo.family.sans,
                        fontSize: typo.size.body,
                        flexShrink: 0,
                      }}
                    >
                      Retry
                    </button>
                  )}
                </div>
              )}
              {journey && (
                <div style={{ flexShrink: 0, marginBottom: 14 }}>
                  <CreativeJourneyPanel
                    journey={journey}
                    business={business}
                    palette={palette}
                    compact
                  />
                </div>
              )}
              <DynamicAssetPool creative={creative} palette={palette} />
            </div>
          ) : (
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                minHeight: 360,
                minWidth: 0,
                gap: 10,
              }}
            >
              {/* Shared controlled preview: live FB iframe over the instant local
                fallback, with an inline "unavailable" banner (+ Retry, + internal-only
                authorizer link) replacing the hook's error toast. `iframeSource` is the
                fresh-gated source so what's on screen is the surface approval is based
                on. */}
              <CreativePreview
                creative={creative}
                iframeSource={iframeSource}
                loading={loadingPreview}
                refreshing={refreshing}
                onRetry={() => refreshPreview()}
                width={PREVIEW_WIDTH}
                height="min(900px, 92vh)"
                frameless
              />
            </div>
          )}
          {!isDynamic && journey && (
            <div
              style={{
                width: JOURNEY_WIDTH,
                flexShrink: 0,
                maxHeight: 'min(900px, 92vh)',
                overflowY: 'auto',
                // A destination link card is one row; center it against the tall
                // ad preview. The lead-form phone fills the column top-down.
                display: 'flex',
                flexDirection: 'column',
                justifyContent: journey.kind === 'destination' ? 'center' : 'flex-start',
              }}
            >
              <CreativeJourneyPanel journey={journey} business={business} palette={palette} />
            </div>
          )}
        </div>
      </PacketModalShell>
    </>
  )
}
